import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Skeleton,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ConversationChannelTypeExtended,
  ConversationMessageType,
} from '@provision/chat-utils';
import { ConversationMessageDispatch } from '@provision/entity-types';
import {
  isConversationAttachmentWithUrl,
  RichTextEditorFile,
} from '@provision/rich-text-editor';
import { memo, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageBox, MessageType } from 'react-chat-elements';
import { DynamicIcon, PartnerTimeZoneTooltip, Tooltip } from 'src/components';
import ShadowRoot from 'src/components/ShadowRoot';
import { useTextWithUI } from 'src/components/TextWithUI/use-text-with-ui';
import { ConversationParticipant, useGroupRules, UserGroupRules } from 'src/graphql';
import { ParticipantSelectorItem } from 'src/pages/Conversations/Chat/ParticipationPanel/types';
import { MAX_CONVERSATION_MESSAGE_HEIGHT_REM } from 'src/pages/Conversations/constants';
import { useDisplayTime } from 'src/utils/hooks';
import { convertNumberToRemNumber } from 'src/utils/html';
import { isSupportedAudioAttachmentContentType } from './attachment-message-content.utils';
import { ChatMessageEditMode } from './chat-message-edit-mode';
import { DispatchStatusTooltip } from './dispatch-status/dispatch-status-tooltip';
import { hasFailedDispatch } from './dispatch-status/dispatch-status.utils';
import { MessageAttachments } from './message-attachments';
import { RedactButton, RedactionControls } from './redaction-controls';
import { useMessageContextMenu } from './use-message-context-menu';
import { isEmailMessageShouldBeDisplayedUnstyled, isFullDocument } from './utils';

interface MessageProps {
  id: string;
  text: string;
  type: MessageType['type'];
  createdAt: string;
  messageType: ConversationMessageType;
  chatRef: RefObject<HTMLDivElement | null>;
  partnerId?: number | null;
  emailFileKey?: string;
  attachments?: RichTextEditorFile[];
  onUpdate?: (message: {
    conversationMessageId: string;
    body: string;
    editedAt?: string;
  }) => void;
  onEdit?: (messageId: string, newBody: string, newAttachments: RichTextEditorFile[]) => void;
  readonly?: boolean;
  isEditable?: boolean;
  channel: ConversationChannelTypeExtended;
  canViewOriginalEmailOnly?: boolean;
  sender?: (ConversationParticipant & { fullName?: string }) | null;
  messageDispatches?: ConversationMessageDispatch[];
  receiver?: ParticipantSelectorItem;
  metaData?: Record<string, Record<string, unknown[]>>;
}

const MessageShowMore = styled(Button, {
  name: 'MessageShowMore',
  shouldForwardProp: (prop) => prop !== 'isCollapsed',
})<{
  isCollapsed: boolean;
}>(({ theme, isCollapsed }) => {
  return {
    marginTop: !isCollapsed ? '0.6rem' : 'unset',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    opacity: 0.8,

    '&:hover': {
      background: 'unset',
    },
  };
});

export const MessageTimestamp = styled(Typography, {
  name: 'MessageTimestamp',
})(({ theme }) => ({
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  fontSize: '0.6rem',
  whiteSpace: 'nowrap',
  minWidth: '2.6rem',
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  marginLeft: 0,
  marginRight: theme.spacing(0.4),
  textAlign: 'center',
}));

const MessageContainer = styled(Stack, {
  name: 'MessageContainer',
  shouldForwardProp: (prop) => prop !== 'isPrivate',
})<{
  isPrivate?: boolean;
}>(({ theme, isPrivate }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '94%',
  justifyContent: 'flex-start',
  alignItems: 'start',
  '&:not(:last-child)': {
    borderBottom: isPrivate
      ? `2px solid ${theme.palette.custom.OV.sys.surfaceContainerLowest}`
      : 'none',
  },

  '&:hover .timestamp': {
    opacity: 1,
  },

  '&:hover .action-menu-button': {
    opacity: 1,
  },
}));

const StyledMessage = styled(MessageBox, {
  name: 'StyledMessage',
  shouldForwardProp: (prop) =>
    !['isCollapsible', 'backgroundColor', 'isCollapsed', 'unstyled'].includes(prop as string),
})<{
  backgroundColor: string;
  isCollapsible: boolean;
  isCollapsed: boolean;
  unstyled: boolean;
}>(({ theme, backgroundColor, isCollapsible, isCollapsed, unstyled }) => {
  const messageStyles = {
    backgroundColor,
    alignSelf: 'flex-start',
  };

  return {
    minWidth: theme.utils.important('auto'),
    maxHeight:
      isCollapsible && isCollapsed ? `${MAX_CONVERSATION_MESSAGE_HEIGHT_REM}rem` : 'unset',
    transition: 'max-height 0.4s',
    '& .rce-mbox': {
      flexShrink: 0,
      padding: 0,
      margin: theme.utils.important('0'),
      minWidth: '1rem',
      boxShadow: 'none',
      ...(!unstyled ? { whiteSpace: 'pre-wrap' } : {}),
      ...messageStyles,

      /* This preserves proper whitespace between paragraphs in messages. */
      '& .rce-mbox-text > div': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      },

      '& .rce-mbox-text::after': {
        content: 'none',
      },
    },

    '&.rce-container-mbox': {
      minWidth: '100px',
      justifyContent: isCollapsible ? 'flex-start' : 'flex-end',
      display: 'flex',
      width: '100%',
      maskImage:
        isCollapsible && isCollapsed
          ? 'linear-gradient(to bottom, #000 50%, transparent 100%)'
          : 'unset',
      overflowX: 'auto',
      overflowY: 'hidden',
    },

    /*
     * Message content styling.
     */
    ...(!unstyled
      ? {
          '& p, & pre': {
            margin: theme.spacing(0),
            whiteSpace: 'pre-wrap',
            minHeight: '1rem',

            '&:only-child': {
              margin: 0,
            },
          },
          '& img': {
            maxWidth: '100%',
            cursor: 'pointer',
            borderRadius: theme.shape.borderRadius,
            objectFit: 'scale-down',
            height: 'auto',
          },

          '& pre': {
            margin: theme.spacing(0, 0.25),
          },
          '& code': {
            backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest,
            borderRadius: '0.25rem',
            boxDecorationBreak: 'clone',
            color: theme.palette.custom.OV.sys.onSurface,
            fontSize: '0.9rem',
            padding: '0.25rem',
          },
        }
      : {}),
    '& .file-message-preview-file-name': {
      color: theme.palette.custom.OV.sys.onSurface,
    },
    '& .file-message-preview-file-size': {
      color: theme.palette.custom.OV.sys.onSurfaceVariant,
    },
  };
});

const StyledMessageContainer = styled(Stack, {
  name: 'StyledMessageContainer',
  shouldForwardProp: (prop) =>
    !['backgroundColor', 'hasWideAttachment', 'isPrivate', 'isEditing', 'isFailed'].includes(
      prop as string,
    ),
})<{
  backgroundColor: string;
  hasWideAttachment: boolean;
  isPrivate: boolean;
  isEditing: boolean;
  isFailed: boolean;
}>(({ theme, isPrivate, backgroundColor, hasWideAttachment, isEditing, isFailed }) => {
  const extraCSSProperties: React.CSSProperties = {};

  if (isPrivate) {
    extraCSSProperties.width = 'calc(100% - 2.75rem)';
    extraCSSProperties.maxWidth = isEditing ? '100%' : '75%';
    extraCSSProperties.borderRadius = 0;
  }

  return {
    position: 'relative',
    backgroundColor,
    borderRadius: theme.shape.borderRadius / 2,
    ...(isFailed
      ? {
          borderLeft: `3px solid ${theme.palette.error.main}`,
          marginBottom: theme.spacing(2.5),
        }
      : {}),
    padding: '0.375rem 0.5625rem',
    maxWidth: '80%',
    ...extraCSSProperties,

    ...(!isEditing && {
      [theme.breakpoints.down('sm')]: {
        ...(hasWideAttachment
          ? { width: 'calc(100% - 2.75rem)', maxWidth: 'calc(100% - 2.75rem)' }
          : { maxWidth: '70%' }),
      },
    }),
  };
});

export const Message = memo(
  ({
    id,
    text,
    type,
    createdAt,
    partnerId,
    attachments,
    messageType,
    emailFileKey,
    chatRef,
    onUpdate,
    onEdit,
    readonly,
    channel,
    messageDispatches,
    sender,
    canViewOriginalEmailOnly,
    receiver,
    isEditable,
    metaData,
  }: MessageProps) => {
    const { displayTime } = useDisplayTime();

    const { checkValidity } = useGroupRules();

    const hasAccessToEditInternalNotes = checkValidity([
      UserGroupRules.AccessToEditInternalNotes,
    ]);

    const [isEditing, setIsEditing] = useState(false);

    const timestamp = useMemo(
      () => (
        <MessageTimestamp className="timestamp">
          <PartnerTimeZoneTooltip date={createdAt} partnerId={partnerId}>
            {displayTime(createdAt)}
          </PartnerTimeZoneTooltip>
        </MessageTimestamp>
      ),
      [createdAt, displayTime, partnerId],
    );

    const theme = useTheme();

    const containerRef = useRef<HTMLDivElement>(null);

    const [isCollapsible, setIsCollapsible] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [messageHeight, setMessageHeight] = useState(0);

    useEffect(() => {
      if (!containerRef.current) {
        return;
      }

      // micro delay to let nested shadow dom render by the time we measure the height
      setTimeout(() => {
        const elementHeight = containerRef.current?.offsetHeight ?? 0;
        const elemHightInRem = convertNumberToRemNumber(elementHeight);
        setIsCollapsible(elemHightInRem > MAX_CONVERSATION_MESSAGE_HEIGHT_REM);
        setMessageHeight(elementHeight);
      }, 100);
    }, [containerRef]);

    /*
     * Conversation messages are displayed in column-reverse order and it leads to upwards grow of the message when it is expanded.
     * This leads to bad user behaviour so start of the messages goes up and becomes not visibile if message is big.
     * To solve it we check difference between open/closed message height and when message is opened - scoll to the beginning of the message programatically.
     */
    const handleCollapse = useCallback(() => {
      setIsCollapsed(!isCollapsed);
      if (!isCollapsed) {
        return;
      }

      const heightDiff = messageHeight - (containerRef.current?.offsetHeight ?? 0);
      chatRef.current?.scrollTo(0, chatRef.current?.scrollTop - heightDiff);
    }, [chatRef, containerRef, isCollapsed, messageHeight]);

    const backgroundColor = useMemo(() => {
      if (messageType === ConversationMessageType.Private) {
        return theme.palette.custom.OV.custom.InternalNotes.noteMessageContainer;
      }

      if (messageType === ConversationMessageType.Engage) {
        return theme.palette.custom.OV.sys.surfaceContainerHighest;
      }

      return theme.palette.custom.OV.sys.tertiaryContainer;
    }, [messageType, theme]);

    const isPrivate = useMemo(
      () => messageType === ConversationMessageType.Private,
      [messageType],
    );

    const unstyled = useMemo(() => {
      if (isPrivate || channel !== ConversationChannelTypeExtended.Email) {
        return false;
      }

      return isEmailMessageShouldBeDisplayedUnstyled(text);
    }, [isPrivate, channel, text]);

    const {
      open,
      isRedacting,
      handleMenuClick,
      handleMenuClose,
      menuAnchorEl,
      selectionCoordinates,
      setSelectionCoordinates,
      handleRedactClick,
      handleOpenOriginalEmailClick,
      handleRedactSelection,
      handleCancelRedaction,
      handleConfirmRedaction,
      handleCopyMessageIdToClipboard,
      redactedText,
    } = useMessageContextMenu({
      id,
      message: text,
      container: containerRef.current,
      onRedactStart: useCallback(() => setIsCollapsed(false), []),
      onConfirm: useCallback(
        (message) => {
          onUpdate?.({ conversationMessageId: id, body: message });
        },
        [onUpdate, id],
      ),
    });

    const {
      text: textWithUI,
      handlers: textWithUIHandlers,
      parentStyles: textWithUIParentStyles,
    } = useTextWithUI(redactedText, metaData);

    const requiresDomScoping = useMemo(() => {
      if (channel !== ConversationChannelTypeExtended.Email) {
        return false;
      }

      return isFullDocument(text);
    }, [channel, text]);

    const messageMenuItems = useMemo(() => {
      const menuItems = [];
      if (isEditable && !readonly && hasAccessToEditInternalNotes) {
        menuItems.push({
          label: 'Edit message',
          action: () => setIsEditing(true),
        });
      }
      if (
        messageDispatches?.length &&
        (!readonly || (canViewOriginalEmailOnly && !!emailFileKey))
      ) {
        if (!readonly) {
          menuItems.push({
            label: 'Redact message',
            action: handleRedactClick,
          });
        }
        if (emailFileKey) {
          menuItems.push({
            label: 'View original email',
            action: handleOpenOriginalEmailClick,
          });
        }

        menuItems.push({
          label: 'Copy message ID',
          action: handleCopyMessageIdToClipboard,
        });
      }
      return menuItems;
    }, [
      canViewOriginalEmailOnly,
      emailFileKey,
      handleCopyMessageIdToClipboard,
      handleOpenOriginalEmailClick,
      handleRedactClick,
      hasAccessToEditInternalNotes,
      isEditable,
      messageDispatches?.length,
      readonly,
    ]);

    const handleCancelEdit = useCallback(() => {
      setIsEditing(false);
    }, []);

    const handleSaveEdit = useCallback(
      (newBody: string, newAttachments: RichTextEditorFile[]) => {
        if (newBody !== text || newAttachments !== attachments) {
          onEdit?.(id, newBody, newAttachments);
        }
        setIsEditing(false);
      },
      [text, attachments, onEdit, id],
    );

    const hasWideAttachment =
      attachments?.some(
        (attachment) =>
          isConversationAttachmentWithUrl(attachment) &&
          isSupportedAudioAttachmentContentType(attachment.contentType),
      ) ?? false;

    const isFailedOutboundMessage =
      messageType === ConversationMessageType.Assist &&
      hasFailedDispatch(messageDispatches ?? []);

    const shouldShowMore = messageMenuItems.length > 0 && !isEditing && !isRedacting;
    return (
      <MessageContainer isPrivate={isPrivate}>
        {timestamp}
        <StyledMessageContainer
          hasWideAttachment={hasWideAttachment}
          isEditing={isEditing}
          isPrivate={isPrivate}
          isFailed={isFailedOutboundMessage}
          backgroundColor={backgroundColor}
          ref={containerRef}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              position: 'absolute',
              right: '-65px',
              top: 0,
            }}
          >
            {!!messageDispatches?.length && messageType === ConversationMessageType.Assist && (
              <Box sx={{ cursor: 'pointer' }}>
                <DispatchStatusTooltip
                  from={{
                    fullName: sender?.fullName || '',
                    email:
                      messageDispatches?.[0]?.participation?.channel?.internalIdentifier ?? '',
                  }}
                  to={receiver}
                  dispatches={messageDispatches}
                />
              </Box>
            )}

            {!!shouldShowMore && (
              <Tooltip title="More">
                <IconButton
                  size="small"
                  className="action-menu-button"
                  onClick={handleMenuClick}
                  sx={{
                    opacity: 0,
                  }}
                >
                  <DynamicIcon name="more_vert" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {!!shouldShowMore && (
            <Menu
              anchorEl={menuAnchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {messageMenuItems.map((item) => (
                <MenuItem
                  key={item.label}
                  onClick={() => {
                    item.action();
                    handleMenuClose();
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          )}
          {isEditing ? (
            <ChatMessageEditMode
              onClose={handleCancelEdit}
              onSave={handleSaveEdit}
              content={text}
              attachments={attachments}
              partnerId={partnerId ?? null}
            />
          ) : (
            <>
              {' '}
              <StyledMessage
                id={id}
                // @ts-expect-error Incorrect component typings
                text={
                  requiresDomScoping ? (
                    <ShadowRoot>
                      <div dangerouslySetInnerHTML={{ __html: textWithUI }} />
                    </ShadowRoot>
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: textWithUI }} />
                  )
                }
                type={type}
                backgroundColor={backgroundColor}
                notch={false}
                isCollapsible={isCollapsible}
                isCollapsed={isCollapsed}
                unstyled={unstyled}
                sx={textWithUIParentStyles}
                {...textWithUIHandlers}
              />
              {isRedacting ? (
                <RedactionControls mt={1} borderTop={1}>
                  <Button
                    size="small"
                    sx={{ marginLeft: 'auto' }}
                    color="inherit"
                    onClick={handleCancelRedaction}
                  >
                    Cancel
                  </Button>
                  <Button size="small" color="primary" onClick={handleConfirmRedaction}>
                    Redact
                  </Button>
                </RedactionControls>
              ) : null}
              {!!attachments?.length && <MessageAttachments attachments={attachments} />}
              {isCollapsible ? (
                <MessageShowMore onClick={handleCollapse} isCollapsed={isCollapsed}>
                  {!isCollapsed ? 'Show Less' : 'Show More'}
                </MessageShowMore>
              ) : null}
              {isFailedOutboundMessage ? (
                <Typography
                  variant="caption"
                  color="error.main"
                  fontSize="0.6875rem"
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: (theme) => theme.spacing(1),
                    mt: 0.25,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Not delivered
                </Typography>
              ) : null}
              <Popover
                open={Boolean(selectionCoordinates)}
                // we are using shadow dome to scope messages that are raw HTML elements
                // therefore it is not possible to use redaction text anchors
                anchorReference="anchorPosition"
                anchorPosition={selectionCoordinates || { top: 0, left: 0 }}
                onClose={() => setSelectionCoordinates(null)}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                slotProps={{
                  paper: {
                    sx: { mt: -1 },
                  },
                }}
              >
                <RedactButton onClick={handleRedactSelection}>Mark as redacted</RedactButton>
              </Popover>
            </>
          )}
        </StyledMessageContainer>
      </MessageContainer>
    );
  },
);

Message.displayName = 'Message';

export const MessageItemSkeleton = () => (
  <Stack direction="column" alignItems="flex-end" mr="1rem">
    <Stack
      ml={1}
      direction="row"
      overflow="hidden"
      gap={1}
      width="100%"
      justifyContent="flex-end"
    >
      <Skeleton width="30%" />
      <Skeleton width="8%" />
      <Skeleton width="15%" />
      <Skeleton variant="circular" width={35} height={35} />
    </Stack>
    <Skeleton width="40%" height="3rem" sx={{ marginRight: '1.5rem' }} />
  </Stack>
);
